import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import qs from 'qs'
import { APIService } from 'api/APIService'
import { getIsAuthenticated, setAuthToken } from 'src/store/app.state.store'
import { HashRouteModal } from 'components/Utility/HashRouteModal/HashRouteModal'
import { SignInForm } from 'components/Forms/SignInForm/SignInForm'
import { SignUpForm, type SignUpResult } from 'components/Forms/SignUpForm/SignUpForm'
import { RecoveryFormController } from 'components/Forms/RecoveryForm/RecoveryFormController'
import { UserProfile } from 'components/Profile/UserProfile/UserProfile'
import { ReviewForm } from 'components/Forms/ReviewForm/ReviewForm'

/** Hashes that need a signed-in client; others are sent to #signin and back */
const PROTECTED_HASHES = ['#profile', '#review']

/** The account modals: sign in, sign up and recovery, or profile and review */
export const AuthController: React.FC = () => {
  const isAuthenticated = useSelector(getIsAuthenticated)
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  const onToken = useCallback((token: string) => {
    dispatch(setAuthToken(token))
  }, [dispatch])

  const handleSignIn = useCallback((token: string) => {
    onToken(token)
    const query = qs.parse(location.search.replace('?', ''))
    navigate(`#${typeof query.to === 'string' ? query.to : ''}`)
  }, [onToken, location.search, navigate])

  const handleSignUp = useCallback(async ({ phone, password }: SignUpResult) => {
    try {
      const token = await APIService.auth.signIn({ phone, password })
      onToken(token)
      navigate('#')
    } catch {
      navigate('#signin')
    }
  }, [onToken, navigate])

  const closeModals = useCallback(() => {
    navigate('#')
  }, [navigate])

  useEffect(() => {
    if (PROTECTED_HASHES.includes(location.hash) && !isAuthenticated) {
      const destination = location.hash.replace('#', '')
      navigate(`?to=${destination}#signin`, { replace: true })
    }
  }, [location.hash, isAuthenticated, navigate])

  if (isAuthenticated) {
    return (
      <>
        <HashRouteModal hash={'#profile'} title={'Личный кабинет'}>
          <UserProfile />
        </HashRouteModal>
        <HashRouteModal hash={'#review'} title={'Оставить отзыв'}>
          <ReviewForm onSuccess={closeModals} />
        </HashRouteModal>
      </>
    )
  }

  return (
    <>
      <HashRouteModal hash={'#signin'} title={'Вход'}>
        <SignInForm onSuccess={handleSignIn} />
      </HashRouteModal>
      <HashRouteModal hash={'#signup'} back={'#signin'} title={'Регистрация'}>
        <SignUpForm onSuccess={handleSignUp} />
      </HashRouteModal>
      <HashRouteModal hash={'#recover'} back={'#signin'} title={'Восстановление пароля'}>
        <RecoveryFormController onSuccess={closeModals} />
      </HashRouteModal>
    </>
  )
}
